import { createApp } from 'vue';
import App from './App.vue';
import router from './router/router';
import axios from 'axios';
import { createMetaManager } from 'vue-meta';
import { createPinia } from 'pinia';
import { useAppStore } from './stores/app';
import { useAuthStore } from './stores/auth';
import { VueReCaptcha } from 'vue-recaptcha-v3';

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

function getValidationErrorMessage(error) {
  const data = error.response?.data;

  if (!data) {
    return 'Wystąpił błąd walidacji.';
  }

  if (data.errors && typeof data.errors === 'object') {
    return Object.values(data.errors)
        .flat()
        .join('\n');
  }

  if (data.message) {
    return data.message;
  }

  return 'Wystąpił błąd walidacji.';
}

async function bootstrap() {
  const pinia = createPinia();
  const app = createApp(App);

  app.use(router);
  app.use(createMetaManager());
  app.use(pinia);
  app.use(VueReCaptcha, {
    siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY
  });

  const appStore = useAppStore(pinia);
  const authStore = useAuthStore(pinia);

  await authStore.initAuth();

  axios.interceptors.response.use(
      async (response) => {
        if (response.config.url.includes('/api/sanctum-user') && response.status === 200 && response.data?.id) {
          await appStore.setIsSubscribed(response.data);
        }
        return response;
      },
      (error) => {
        const requestUrl = error.config?.url || '';

        if (error.response?.status === 401) {
          if (
              requestUrl.includes('/api/sanctum-login') ||
              requestUrl.includes('/api/sanctum-logout') ||
              requestUrl.includes('/api/sanctum-user')
          ) {
            return Promise.reject(error);
          }

          if (requestUrl.includes('/login')) {
            appStore.setIsAlert(
                'Twoje konto jest nieaktywne. Możesz je aktywować za pomocą linku wysłanego na podany adres email'
            );
          } else {
            window.dispatchEvent(new CustomEvent('app:session-expired'));
            router.push('/login').then(() => {
              appStore.setIsAlert('Sesja wygasła. Zaloguj się ponownie.');
              authStore.logout();
            });
          }
        } else if (error.response?.status === 400) {
          if (error.config.url.includes('/CalendarItemAddUser')) {
            appStore.setIsAlert('Wystąpił błąd, bądź usługodawca nie może przyjąć zaliczki. Skontaktuj się z nim.');
          } else if (error.config.url.includes('/AddBusinessToFirm')) {
            appStore.setIsAlert('Nie znaleziono specjalisty, bądź użytkownik otrzymał już zaproszenie.');
          } else {
            appStore.setIsAlert();
          }
        } else if (error.response?.status === 422) {
          appStore.setIsAlert(getValidationErrorMessage(error));
        } else if (error.response?.status === 429) {
          appStore.setIsAlert('Błąd, spróbuj ponownie później');
        } else if (
            error.response?.status === 404 &&
            error.config.url.includes('/login')
        ) {
          appStore.setIsAlert('Zły adres e-mail lub hasło');
        } else if (error.response?.status === 500) {
          if (error.config?.url?.includes('/AddBusinessToFirmConfirmation')) {
            appStore.setIsAlert('Niepoprawny numer zaproszenia.');
          } else {
            appStore.setIsAlert();
          }
        }

        return Promise.reject(error);
      }
  );

  app.mount('#app');
}

bootstrap();